import type { EncyclopediaEntry } from '@/core/models';
import { equipmentTypeTitle, seasonTitle } from '@/core/models';

export function entryRowHighlights(entry: EncyclopediaEntry): string[] {
  if (entry.cropProfile) {
    return [
      formatSeasons(entry.cropProfile.seasons),
      `${entry.cropProfile.maturityDays}天`,
      entry.cropProfile.regrowDays ? `复收 ${entry.cropProfile.regrowDays}天` : '单收',
      `售价 ${entry.cropProfile.sellPrice}g`,
    ];
  }

  if (entry.fishProfile) {
    return [
      formatSeasons(entry.fishProfile.seasons),
      fishWeatherTitle[entry.fishProfile.weather],
      entry.fishProfile.timeWindows.map(formatTimeWindow).join('/'),
      `难度 ${entry.fishProfile.difficulty}`,
    ];
  }

  if (entry.residentProfile) {
    const lovedGifts = entry.residentProfile.giftTastes.find((group) => group.taste === 'love')?.items.slice(0, 2) ?? [];
    return [
      `生日 ${seasonTitle[entry.residentProfile.birthdaySeason]}${entry.residentProfile.birthdayDay}日`,
      entry.residentProfile.residence,
      entry.residentProfile.marriageable ? '可结婚' : '不可结婚',
      ...(lovedGifts.length > 0 ? [`最爱 ${lovedGifts.join('、')}`] : []),
    ];
  }

  if (entry.topicID === 'farming.artisan') {
    return compact([
      formatFact(entry, '输入'),
      formatFact(entry, '产出'),
      factValue(entry, '定位'),
    ]);
  }

  if (entry.topicID === 'items.tools') {
    const conditions = conditionValues(entry, '使用定位');
    return compact([
      conditions.get('路线'),
      conditions.get('获取/升级'),
      conditions.get('注意'),
    ]);
  }

  if (entry.topicID === 'farming.buildings') {
    const conditions = conditionValues(entry, '建造定位');
    return compact([
      conditions.get('容量/功能'),
      conditions.get('路线'),
      conditions.get('阶段'),
    ]);
  }

  if (entry.topicID === 'farming.animals') {
    const conditions = conditionValues(entry, '饲养定位');
    return compact([
      factValue(entry, '住所'),
      formatFact(entry, '来源'),
      formatFact(entry, '产出'),
      conditions.get('产物节奏'),
    ]);
  }

  if (entry.topicID === 'farming.fruit-trees') {
    const conditions = conditionValues(entry, '种植定位');
    return compact([
      factValue(entry, '结果季节'),
      formatFact(entry, '果实'),
      formatRenamedFact(entry, '成熟时间', '成熟'),
      conditions.get('成长空间'),
    ]);
  }

  if (entry.topicID === 'farming.maps') {
    const conditions = conditionValues(entry, '地图定位');
    return compact([
      factValue(entry, '侧重点'),
      factValue(entry, '选择时机'),
      conditions.get('布局重点'),
    ]);
  }

  if (entry.topicID === 'farming.greenhouse') {
    const conditions = conditionValues(entry, '温室定位');
    return compact([
      factValue(entry, '重点'),
      conditions.get('核心价值'),
      conditions.get('阶段') ?? conditions.get('布局重点'),
    ]);
  }

  if (entry.topicID === 'farming.selling') {
    const conditions = conditionValues(entry, '售卖定位');
    return compact([
      factValue(entry, '重点'),
      conditions.get('适用') ?? factValue(entry, '适用'),
      conditions.get('即时性'),
    ]);
  }

  if (entry.topicID.startsWith('systems.')) {
    const conditions = conditionValues(entry, '机制定位');
    return compact([
      factValue(entry, '定位'),
      conditions.get('核心值'),
      conditions.get('高收益'),
      conditions.get('限制'),
    ]);
  }

  if (explorationRouteTopicIDs.has(entry.topicID)) {
    const conditions = conditionValues(entry, '探索定位');
    return compact([
      factValue(entry, '位置/层数'),
      conditions.get('目标'),
      conditions.get('路线'),
      conditions.get('节奏'),
    ]);
  }

  if (entry.topicID === 'exploration.combat') {
    const conditions = conditionValues(entry, '机制定位');
    return compact([
      factValue(entry, '定位'),
      conditions.get('目标'),
      conditions.get('路线'),
      conditions.get('节奏'),
    ]);
  }

  if (entry.monsterProfile) {
    const dropNames = unique(entry.monsterProfile.drops.map((drop) => drop.name)).slice(0, 2);
    return compact([
      `生命 ${entry.monsterProfile.health}`,
      `伤害 ${entry.monsterProfile.damage}`,
      `防御 ${entry.monsterProfile.defense}`,
      dropNames.length > 0 ? `掉落 ${dropNames.join('、')}` : undefined,
    ]);
  }

  if (entry.equipmentProfile) {
    const equipment = entry.equipmentProfile;
    if (equipment.type === 'weapon') {
      return compact([
        '武器',
        equipment.minDamage !== undefined && equipment.maxDamage !== undefined
          ? `伤害 ${equipment.minDamage}-${equipment.maxDamage}`
          : undefined,
        equipment.speed !== undefined ? `速度 ${equipment.speed}` : undefined,
        equipment.critChance !== undefined ? `暴击 ${formatPercent(equipment.critChance)}` : undefined,
      ]);
    }

    if (equipment.type === 'boots') {
      return compact([
        '鞋靴',
        equipment.defense !== undefined ? `防御 ${equipment.defense}` : undefined,
        equipment.immunity !== undefined ? `免疫 ${equipment.immunity}` : undefined,
        equipment.price !== undefined ? `价格 ${equipment.price}g` : undefined,
      ]);
    }

    return compact([
      equipmentTypeTitle[equipment.type],
      equipment.price !== undefined ? `价格 ${equipment.price}g` : undefined,
    ]);
  }

  if (entry.recipeProfile) {
    const recipe = entry.recipeProfile;
    if (recipe.type === 'cooking') {
      return compact([
        `材料 ${recipe.ingredients.length}种`,
        recipe.energy !== undefined ? `能量 ${recipe.energy}` : undefined,
        recipe.health !== undefined ? `生命 ${recipe.health}` : undefined,
        recipe.buffs?.[0] ? `${recipe.buffs[0].label} ${recipe.buffs[0].value}` : undefined,
      ]);
    }

    return compact([
      `材料 ${recipe.ingredients.length}种`,
      `产量 ${recipe.outputQuantity}`,
      recipe.unlockDescription,
    ]);
  }

  if (entry.topicID === 'places.shops') {
    return compact([
      factValue(entry, '店主'),
      factValue(entry, '营业'),
      formatFact(entry, '主要商品'),
    ]);
  }

  if (entry.topicID === 'places.hours') {
    return compact([
      factValue(entry, '营业'),
      factValue(entry, '地点'),
      factValue(entry, '关闭/限制'),
    ]);
  }

  if (['places.town', 'places.desert', 'places.ginger-island'].includes(entry.topicID)) {
    return compact([
      firstFactValue(entry, ['区域', '解锁', '店主']),
      formatFirstFact(entry, ['主要设施', '主要用途', '常用商品']),
      formatFirstFact(entry, ['常用路线', '通行', '限制']),
    ]);
  }

  if (entry.topicID === 'fishing.locations') {
    const conditions = conditionValues(entry, '钓点定位');
    return compact([
      factValue(entry, '位置'),
      formatFact(entry, '常见鱼类'),
      conditions.get('路线'),
    ]);
  }

  if (entry.topicID === 'fishing.bait') {
    const conditions = conditionValues(entry, '鱼饵定位');
    return compact([
      formatFact(entry, '效果'),
      formatFact(entry, '用法'),
      conditions.get('路线'),
    ]);
  }

  if (entry.topicID === 'fishing.tackle') {
    const conditions = conditionValues(entry, '渔具定位');
    return compact([
      formatFact(entry, '效果'),
      formatFact(entry, '适用'),
      conditions.get('路线'),
    ]);
  }

  if (entry.topicID === 'fishing.crab-pots') {
    const conditions = conditionValues(entry, '蟹笼定位');
    return compact([
      formatFact(entry, '规则'),
      formatFact(entry, '产出'),
      conditions.get('路线'),
    ]);
  }

  if (entry.topicID === 'tasks.quests') {
    return compact([
      factValue(entry, '类型'),
      formatFact(entry, '目标'),
      formatRenamedFact(entry, '金币奖励', '奖励'),
    ]);
  }

  if (entry.topicID === 'tasks.bundles') {
    return compact([
      factValue(entry, '房间'),
      factValue(entry, '提交要求'),
      formatRenamedFact(entry, '完成奖励', '奖励'),
    ]);
  }

  if (entry.topicID === 'tasks.achievements') {
    return compact([
      factValue(entry, '达成条件'),
      formatRenamedFact(entry, '前置成就', '前置'),
      formatRenamedFact(entry, '未完成时可见', '可见'),
    ]);
  }

  if (entry.topicID === 'tasks.special-orders') {
    return compact([
      factValue(entry, '委托人'),
      factValue(entry, '期限'),
      formatFact(entry, '可重复'),
      formatFact(entry, '奖励'),
      formatFact(entry, '出现条件'),
    ]);
  }

  if (entry.topicID === 'items.forage') {
    return compact([
      factValue(entry, '分类'),
      formatFact(entry, '基础售价'),
      formatRenamedFact(entry, '可作为礼物', '礼物'),
    ]);
  }

  if (entry.topicID === 'items.minerals') {
    return compact([
      factValue(entry, '分类'),
      formatFact(entry, '基础售价'),
      formatRenamedFact(entry, '宝石复制机', '复制'),
    ]);
  }

  if (entry.topicID === 'items.artifacts') {
    return compact([
      formatFact(entry, '类型'),
      formatFact(entry, '售价'),
      formatFact(entry, '用途'),
    ]);
  }

  if (entry.topicID === 'items.trees') {
    const conditions = conditionValues(entry, '树木定位');
    return compact([
      factValue(entry, '关键物品'),
      factValue(entry, '速查'),
      conditions.get('路线'),
    ]);
  }

  if (entry.topicID === 'items.furniture') {
    const conditions = conditionValues(entry, '摆放定位');
    return compact([
      factValue(entry, '类型'),
      formatFact(entry, '价格'),
      conditions.get('获取'),
    ]);
  }

  if (socialMechanicTopicIDs.has(entry.topicID)) {
    const conditions = conditionValues(entry, '机制定位');
    return compact([
      factValue(entry, '定位'),
      factValue(entry, '速查'),
      conditions.get('高收益') ?? conditions.get('核心值'),
    ]);
  }

  return [];
}

const socialMechanicTopicIDs = new Set([
  'social.gifts',
  'social.friendship',
  'social.marriage',
  'social.children',
]);

const explorationRouteTopicIDs = new Set([
  'exploration.mines',
  'exploration.skull-cavern',
  'exploration.volcano',
]);

const fishWeatherTitle = {
  sunny: '晴天',
  rainy: '雨天',
  any: '任意天气',
} as const;

function formatSeasons(seasons: readonly (keyof typeof seasonTitle)[]) {
  return seasons.map((season) => seasonTitle[season]).join('/');
}

function formatTimeWindow(window: { start: number; end: number }) {
  return `${formatClock(window.start)}-${formatClock(window.end)}`;
}

function formatClock(value: number) {
  const hour = Math.floor(value / 100);
  const minute = value % 100;
  return `${hour}:${minute.toString().padStart(2, '0')}`;
}

function compact(values: Array<string | undefined>) {
  return values.filter((value): value is string => Boolean(value));
}

function unique(values: readonly string[]) {
  return Array.from(new Set(values));
}

function formatPercent(value: number) {
  return `${Math.round(value * 100)}%`;
}

function factValue(entry: EncyclopediaEntry, label: string) {
  return entry.facts.find((fact) => fact.label === label)?.value;
}

function formatFact(entry: EncyclopediaEntry, label: string) {
  const value = factValue(entry, label);
  return value ? `${label} ${value}` : undefined;
}

function firstFactValue(entry: EncyclopediaEntry, labels: readonly string[]) {
  for (const label of labels) {
    const value = factValue(entry, label);
    if (value) return value;
  }
  return undefined;
}

function formatFirstFact(entry: EncyclopediaEntry, labels: readonly string[]) {
  for (const label of labels) {
    const value = factValue(entry, label);
    if (value) return `${label} ${value}`;
  }
  return undefined;
}

function formatRenamedFact(entry: EncyclopediaEntry, label: string, displayLabel: string) {
  const value = factValue(entry, label);
  return value ? `${displayLabel} ${value}` : undefined;
}

function conditionValues(entry: EncyclopediaEntry, title: string) {
  const block = entry.contentBlocks?.find(
    (contentBlock) => contentBlock.type === 'condition-list' && contentBlock.title === title,
  );
  const values = new Map<string, string>();

  if (!block || block.type !== 'condition-list') return values;

  for (const condition of block.conditions) {
    const separatorIndex = condition.indexOf('：');
    if (separatorIndex <= 0) continue;
    values.set(condition.slice(0, separatorIndex), condition.slice(separatorIndex + 1));
  }

  return values;
}
